import React from "react";
import PageHero from "../components/PageHero";
import PartnersMarquee from "../components/PartnersMarquee";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Globe2, Factory, Lightbulb } from "lucide-react";

const STATS = [
  { value: "16+", label: "Years in market" },
  { value: "2.4M", label: "Luminaires shipped" },
  { value: "9", label: "EU countries served" },
  { value: "612", label: "Active trade buyers" },
];

const TIMELINE = [
  { year: "2008", title: "Founded in Rīga", text: "Leduro starts as a 3-person team importing the first generation of LED retrofits to Latvia." },
  { year: "2013", title: "Own product line", text: "We launch the first Leduro-branded SKUs: PAR16 and A60 retrofits with a 3-year warranty." },
  { year: "2017", title: "Trade-only model", text: "Pivot to a pure B2B model — wholesalers, contractors and facility managers across the Baltics." },
  { year: "2021", title: "PROFlood series", text: "Introduction of cast aluminium IP65/IK08 outdoor floodlights, 10W to 200W." },
  { year: "2026", title: "EU expansion", text: "Nine countries served, two regional warehouses, in-house photometry lab." },
];

export default function AboutUs() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="Lighting professionals supplying lighting professionals."
        subtitle="Leduro is a Baltic B2B LED supplier. We sell only to companies — wholesalers, contractors and facilities — and we treat every SKU as a long-term commitment, not a season's product."
        crumbs={[{ label: "About Us" }]}
      />

      {/* Intro */}
      <section className="bg-white py-20 md:py-28">
        <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="leduro-eyebrow">— Our approach</span>
            <h2 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-4xl text-balance">
              We engineer fewer products, but we stand behind each one for years.
            </h2>
            <div className="mt-6 space-y-4 text-leduro-muted leading-relaxed">
              <p>
                Most LED suppliers chase catalogue size. We chase reliability. Our 250-SKU range is built to cover
                90% of trade installations — from residential downlights to 200W outdoor projectors — with
                photometric data you can actually open in DIALux.
              </p>
              <p>
                Our drivers, LEDs and housings are tested in our own Rīga lab before they hit stock. When a batch
                doesn't pass, it doesn't ship. It's that simple.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-px bg-leduro-border border border-leduro-border">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white p-6 lg:p-7" data-testid={`about-stat-${s.label}`}>
                  <div className="text-leduro-orange font-heading font-black text-4xl lg:text-5xl leading-none">
                    {s.value}
                  </div>
                  <div className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-leduro-navy">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-leduro-surface py-20 md:py-24">
        <div className="leduro-container">
          <span className="leduro-eyebrow">— Principles</span>
          <h2 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-4xl max-w-2xl">
            Four principles every Leduro SKU has to pass.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-leduro-border border border-leduro-border">
            {[
              { Icon: Lightbulb, title: "Real photometrics", text: "Every datasheet is measured, not extrapolated. We publish IES / LDT files openly." },
              { Icon: Factory, title: "Single-supplier driver", text: "All drivers come from one of three audited factories. No mystery components." },
              { Icon: Building2, title: "Spec-compliant", text: "EN 60598, EN 62471, EN 55015 and CB scheme reports for every active SKU." },
              { Icon: Globe2, title: "Stocked in EU", text: "Inventory in Rīga and Tallinn — no offshore-shipping surprises on lead times." },
            ].map(({ Icon, title, text }, i) => (
              <div key={i} className="bg-white p-6 lg:p-8" data-testid={`principle-${i}`}>
                <Icon className="h-7 w-7 text-leduro-orange" />
                <h3 className="mt-5 font-heading font-bold text-leduro-navy text-lg">{title}</h3>
                <p className="mt-2 text-sm text-leduro-muted leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-leduro-navy text-white py-20 md:py-28 noise-overlay">
        <div className="absolute inset-0 grid-lines opacity-25 pointer-events-none" />
        <div className="relative leduro-container">
          <span className="leduro-eyebrow text-leduro-orange">— Journey</span>
          <h2 className="mt-3 font-heading font-black tracking-tighter text-white text-3xl md:text-4xl">
            Sixteen years, one mission.
          </h2>

          <div className="mt-14 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/20" />
            <div className="space-y-12">
              {TIMELINE.map((t, i) => (
                <div
                  key={t.year}
                  data-testid={`timeline-${t.year}`}
                  className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`}
                >
                  <div className="md:text-right pl-10 md:pl-0 md:pr-12">
                    <span className="font-heading font-black text-leduro-orange text-3xl md:text-4xl">{t.year}</span>
                  </div>
                  <div className="pl-10 md:pl-12 relative">
                    <span className="absolute left-1.5 md:left-[-6px] top-2.5 h-3 w-3 bg-leduro-orange rotate-45" />
                    <h3 className="font-heading font-bold text-white text-xl">{t.title}</h3>
                    <p className="mt-2 text-white/70 text-sm leading-relaxed max-w-md">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16 flex justify-center">
            <Link to="/where-to-find" className="leduro-btn-primary" data-testid="about-cta">
              Become a trade partner <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <PartnersMarquee />
    </div>
  );
}
